import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import { Slot } from '@radix-ui/react-slot'

const glassButtonVariants = cva(
  cn(
    'relative inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl cursor-pointer',
    'text-sm font-medium transition-all duration-300 ease-out',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent',
    'disabled:pointer-events-none disabled:opacity-50',
    'hover:scale-105 active:scale-95',
    '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0'
  ),
  {
    variants: {
      mode: {
        glass: '',
        solid: '',
      },
      variant: {
        default: cn(
          'bg-white/50 dark:bg-white/10 backdrop-blur-xl border border-black/10 dark:!border-transparent text-foreground',
          'shadow-[0_4px_16px_rgba(0,0,0,0.1)] dark:shadow-[0_4px_16px_rgba(0,0,0,0.2)]',
          'hover:bg-white/70 hover:border-black/15 dark:hover:bg-white/30 dark:hover:!border-transparent',
          'before:absolute before:inset-0 before:rounded-xl',
          'before:bg-linear-to-b before:from-white/20 before:to-transparent before:pointer-events-none'
        ),
        primary: cn(
          'bg-linear-to-r from-cyan-500/40 via-blue-500/40 to-purple-500/40',
          'backdrop-blur-xl border border-black/10 dark:!border-transparent text-foreground',
          'shadow-[0_4px_16px_rgba(59,130,246,0.25)]',
          'hover:shadow-[0_4px_24px_rgba(59,130,246,0.4)]',
          'hover:from-cyan-500/50 hover:via-blue-500/50 hover:to-purple-500/50',
          'before:absolute before:inset-0 before:rounded-xl',
          'before:bg-linear-to-b before:from-white/20 before:to-transparent before:pointer-events-none'
        ),
        outline: cn(
          'bg-transparent backdrop-blur-sm border-2 border-black/15 dark:!border-transparent text-foreground',
          'hover:bg-black/5 hover:border-black/25 dark:hover:bg-white/10 dark:hover:!border-transparent'
        ),
        ghost: cn(
          'bg-transparent text-muted-foreground',
          'hover:bg-black/5 dark:hover:bg-white/10 hover:text-foreground'
        ),
        destructive: cn(
          'bg-red-500/15 dark:bg-red-500/30 backdrop-blur-xl border border-red-600/30 dark:!border-transparent text-red-700 dark:text-red-200',
          'shadow-[0_4px_16px_rgba(239,68,68,0.15)] dark:shadow-[0_4px_16px_rgba(239,68,68,0.3)]',
          'hover:bg-red-500/25 hover:border-red-600/50 dark:hover:bg-red-500/40 dark:hover:!border-transparent',
          'before:absolute before:inset-0 before:rounded-xl',
          'before:bg-linear-to-b before:from-white/10 before:to-transparent before:pointer-events-none'
        ),
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-8 px-3 text-xs',
        lg: 'h-12 px-6 text-base',
        icon: 'h-10 w-10',
      },
    },
    compoundVariants: [
      {
        mode: 'solid',
        variant: 'default',
        className: cn(
          'bg-white/90 dark:bg-gray-800',
          'border-gray-200 dark:border-gray-700',
          'text-gray-900 dark:text-white',
          'backdrop-blur-none shadow-sm',
          'hover:bg-white dark:hover:bg-gray-700',
          'before:hidden'
        ),
      },
      {
        mode: 'solid',
        variant: 'primary',
        className: cn(
          'bg-[#E66A15] hover:bg-[#E66A15]/90 dark:bg-[#FF791A] dark:hover:bg-[#FF791A]/90',
          'border-[#E66A15] dark:border-[#FF791A]',
          'text-white',
          'backdrop-blur-none shadow-md',
          'before:hidden'
        ),
      },
      {
        mode: 'solid',
        variant: 'outline',
        className: cn(
          'bg-transparent hover:bg-gray-100 dark:hover:bg-gray-800',
          'border-2 border-gray-300 dark:border-gray-600',
          'text-gray-900 dark:text-white',
          'backdrop-blur-none'
        ),
      },
      {
        mode: 'solid',
        variant: 'ghost',
        className: cn(
          'bg-transparent hover:bg-gray-100 dark:hover:bg-gray-800',
          'text-gray-700 dark:text-gray-200',
          'backdrop-blur-none'
        ),
      },
      {
        mode: 'solid',
        variant: 'destructive',
        className: cn(
          'bg-[#E21C3D] hover:bg-[#E21C3D]/90',
          'border-[#E21C3D]',
          'text-white',
          'backdrop-blur-none shadow-md',
          'before:hidden'
        ),
      },
    ],
    defaultVariants: {
      mode: 'glass',
      variant: 'default',
      size: 'default',
    },
  }
)

export interface GlassButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof glassButtonVariants> {
  glowEffect?: boolean
  asChild?: boolean
}

const GlassButton = React.forwardRef<HTMLButtonElement, GlassButtonProps>(
  (
    {
      className,
      mode,
      variant,
      asChild = false,
      size,
      glowEffect = false,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button'

    // If glowEffect is enabled, wrap in a container for the glow
    if (glowEffect && mode === 'glass') {
      return (
        <div className="relative inline-block">
          <div className="absolute -inset-1 rounded-xl bg-linear-to-r from-cyan-500/30 via-blue-500/30 to-purple-500/30 blur-lg opacity-70 transition-opacity group-hover:opacity-100" />
          <Comp
            className={cn(
              glassButtonVariants({ mode, variant, size, className })
            )}
            ref={ref}
            {...props}
          >
            {asChild ? (
              children
            ) : (
              <span className="relative z-10 flex items-center gap-2">
                {children}
              </span>
            )}
          </Comp>
        </div>
      )
    }

    // No wrapper when glowEffect is disabled (supports absolute positioning)
    return (
      <Comp
        className={cn(glassButtonVariants({ mode, variant, size, className }))}
        ref={ref}
        {...props}
      >
        {asChild ? (
          children
        ) : (
          <span className="relative z-10 flex items-center gap-2">
            {children}
          </span>
        )}
      </Comp>
    )
  }
)
GlassButton.displayName = 'GlassButton'

export { GlassButton, glassButtonVariants }
